import React, { useState } from 'react';

// Step components
const PersonalDetails = ({ formData, handleChange, nextStep }) => {
  const isValidReferenceNumber = (ref) => {
    const nsgsoPattern = /^NSGSO[89]\d{2}$/;
    const nsgcbPattern = /^NSGCB[67]\d{2}$/;
    return nsgsoPattern.test(ref) || nsgcbPattern.test(ref);
  };

  const isStepValid = () => {
    return (
      formData.referenceNumber?.trim() !== '' &&
      isValidReferenceNumber(formData.referenceNumber) &&
      formData.firstName?.trim() !== '' &&
      formData.surname?.trim() !== '' &&
      formData.email?.trim() !== '' &&
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    );
  };

  const handleReferenceChange = (e) => {
    // Convert to uppercase as user types
    const uppercaseValue = e.target.value.toUpperCase();
    handleChange({
      target: {
        name: 'referenceNumber',
        value: uppercaseValue
      }
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold mb-4">Completing this Application Form</h2>
        <p>Your offer to be part of the NSGSO or NSGCB 2026 has been sent to you by email.</p><br />

        <p>Please complete the form below to inform us whether you would like to accept this place or not <b>by 27th December 2025.</b> Offers may be made to other people if you have not completed both this form and paid your deposit by this date as we have had to turn down applicants this year.</p><br />

        <p>Any queries email Jill Bowman NSGSO and NSGCB Manager on <a href="mailto:jill@nsgso-nsgcb.com">jill@nsgso-nsgcb.com</a></p><br />
      </div>

      <h2 className="text-xl font-bold mb-4">Personal Details</h2>
      <div>
        <label className="block mb-2">
          What is your reference number? (you will find this on your offer email. If you have been before this is a new number for 2026):
          <input
            type="text"
            name="referenceNumber"
            value={formData.referenceNumber || ''}
            onChange={handleReferenceChange}
            style={{ textTransform: 'uppercase' }}  // This will show uppercase while typing
            className={`w-full p-2 border rounded mt-1 ${!formData.referenceNumber?.trim() || !isValidReferenceNumber(formData.referenceNumber)
                ? 'border-red-500'
                : 'border-gray-300'
              }`}
            required
          />
          {!formData.referenceNumber?.trim() && (
            <span className="text-red-500 text-sm">Reference number is required</span>
          )}
          {formData.referenceNumber?.trim() && !isValidReferenceNumber(formData.referenceNumber) && (
            <span className="text-red-500 text-sm">
              Reference number must be in format NSGSO8xx, NSGSO9xx, NSGCB6xx, or NSGCB7xx without any spaces and all Capitals.
            </span>
          )}
        </label>
      </div>

      <div>
        <label className="block mb-2">
          First Name:
          <input
            type="text"
            name="firstName"
            value={formData.firstName || ''}
            onChange={handleChange}
            className={`w-full p-2 border rounded mt-1 ${formData.firstName?.trim() === '' ? 'border-red-500' : 'border-gray-300'
              }`}
            required
          />
          {formData.firstName?.trim() === '' && (
            <span className="text-red-500 text-sm">First name is required</span>
          )}
        </label>
      </div>

      <div>
        <label className="block mb-2">
          Surname:
          <input
            type="text"
            name="surname"
            value={formData.surname || ''}
            onChange={handleChange}
            className={`w-full p-2 border rounded mt-1 ${formData.surname?.trim() === '' ? 'border-red-500' : 'border-gray-300'
              }`}
            required
          />
          {formData.surname?.trim() === '' && (
            <span className="text-red-500 text-sm">Surname is required</span>
          )}
        </label>
      </div>

      <div>
        <label className="block mb-2">
          What is your email address (please check spelling)?:
          <input
            type="email"
            name="email"
            value={formData.email || ''}
            onChange={handleChange}
            className={`w-full p-2 border rounded mt-1 ${!formData.email?.trim() || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
              ? 'border-red-500'
              : 'border-gray-300'
              }`}
            required
          />
          {!formData.email?.trim() && (
            <span className="text-red-500 text-sm">Email is required</span>
          )}
          {formData.email?.trim() && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email) && (
            <span className="text-red-500 text-sm">Please enter a valid email address</span>
          )}
        </label>
      </div>

      <div>
        <label className="block mb-2">
          What is your parent's email address (Compulsory for U18; optional for O18)?
          <input
            type="email"
            name="parentEmail"
            value={formData.parentEmail || ''}
            onChange={handleChange}
            className="w-full p-2 border rounded mt-1"
          />
        </label>
      </div>

      <button
        onClick={nextStep}
        disabled={!isStepValid()}
        className={`w-full py-2 px-4 rounded ${isStepValid()
          ? 'bg-nsg-pink text-white hover:bg-nsg-pale-blue'
          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
      >
        Next Step
      </button>
    </div>
  );
};

const CourseSelection = ({ formData, handleChange, nextStep, prevStep }) => {
  const isStepValid = () => {
    if (!formData.acceptPlace) return false;

    if (formData.acceptPlace === 'yes') {
      return formData.ensemble && formData.instruments?.trim() !== '';
    }

    if (formData.acceptPlace === 'maybe') {
      return formData.maybeReason?.trim() !== '';
    }

    return true; // If declining, no additional validation needed
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4">Course Selection</h2>

      <div className="space-y-2">
        <p className="font-semibold">Would you like to accept the place offered for NSGSO/NSGCB 2026?</p>
        <div className="space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="acceptPlace"
              value="yes"
              checked={formData.acceptPlace === 'yes'}
              onChange={handleChange}
              className="mr-2"
            />
            Yes
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="acceptPlace"
              value="no"
              checked={formData.acceptPlace === 'no'}
              onChange={handleChange}
              className="mr-2"
            />
            No
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="acceptPlace"
              value="maybe"
              checked={formData.acceptPlace === 'maybe'}
              onChange={handleChange}
              className="mr-2"
            />
            Maybe
          </label>
        </div>
      </div>

      {formData.acceptPlace === 'yes' && (
        <>
          <div className="space-y-2">
            <p className="font-semibold">Is this for the NSGSO or NSGCB?</p>
            <div className="space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ensemble"
                  value="NSGSO"
                  checked={formData.ensemble === 'NSGSO'}
                  onChange={handleChange}
                  className="mr-2"
                />
                NSGSO
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ensemble"
                  value="NSGCB"
                  checked={formData.ensemble === 'NSGCB'}
                  onChange={handleChange}
                  className="mr-2"
                />
                NSGCB
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ensemble"
                  value="waiting"
                  checked={formData.ensemble === 'waiting'}
                  onChange={handleChange}
                  className="mr-2"
                />
                I am waiting to find out which ensemble
              </label>
            </div>
          </div>

          <div>
            <label className="block mb-2">
              Please state the instrument(s) that you have been accepted for:
              <input
                type="text"
                name="instruments"
                value={formData.instruments}
                onChange={handleChange}
                className="w-full p-2 border rounded mt-1"
                required
              />
            </label>
          </div>
        </>
      )}

      {formData.acceptPlace === 'no' && (
        <div>
          <label className="block mb-2">
            Thank you for letting us know. If you are willing to let us know the reason why you have decided not to accept your place that information is useful to us for monitoring purposes; however it is entirely optional. If you change your mind or your circumstances change please contact Jill Bowman at jill@nsgso-nsgcb.com and we can discuss a way forward.
            <textarea
              name="declineReason"
              value={formData.declineReason}
              onChange={handleChange}
              className="w-full p-2 border rounded mt-1"
            />
          </label>
        </div>
      )}

      {formData.acceptPlace === 'maybe' && (
        <div>
          <label className="block mb-2">
            Thank you for letting us know. Please state below the potential problem and if you know an approximate date when you might be able to accept your place or not. Jill Bowman will contact you shortly to discuss and to talk about a way forward.
            <br /> Do you have any other queries about your offer?
            <textarea
              name="maybeReason"
              value={formData.maybeReason}
              onChange={handleChange}
              className="w-full p-2 border rounded mt-1"
              required
            />
          </label>
        </div>
      )}

      <div className="flex justify-between mt-4">
        <button
          onClick={prevStep}
          className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
        >
          Previous
        </button>
        <button
          onClick={nextStep}
          disabled={!isStepValid()}
          className={`py-2 px-4 rounded ${isStepValid()
            ? 'bg-nsg-pink text-white hover:bg-nsg-pale-blue'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

const UniformRequirements = ({ formData, handleChange, nextStep, prevStep }) => {
  const handleUniformChange = (e) => {
    const { name, value } = e.target;

    // If changing uniform type, clear dependent fields
    if (name === 'uniformType') {
      handleChange({
        target: {
          name: 'uniformType',
          value: value
        }
      });

      // Clear dependent fields if not option 5
      if (value !== '5') {
        handleChange({
          target: {
            name: 'neckerLost',
            value: ''
          }
        });
        handleChange({
          target: {
            name: 'woggleLost',
            value: ''
          }
        });
        handleChange({
          target: {
            name: 'poloTopLost',
            value: ''
          }
        });
        handleChange({
          target: {
            name: 'poloSize',
            value: ''
          }
        });
      }
    } else {
      // For all other changes, use the original handler
      handleChange(e);
    }
  };

  const isStepValid = () => {
    // Only validate if user is accepting the place
    if (formData.acceptPlace !== 'yes') return true;

    // Check if uniform agreement is checked
    if (!formData.uniformAgreement) return false;

    // Check if uniform type is selected
    if (!formData.uniformType) return false;

    // Additional validation for lost items (type 5)
    if (formData.uniformType === '5') {
      // Validate woggle selection
      if (!formData.woggleLost) return false;

      // Validate polo top selection
      if (!formData.poloTopLost) return false;

      // If they want a new polo top, size must be selected
      if (formData.poloTopLost === 'yes' && !formData.poloSize) return false;
    }

    // Validate polo size for new members or size changes
    if (['2', '3', '4'].includes(formData.uniformType) && !formData.poloSize) {
      return false;
    }

    return true;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4">Uniform Requirements</h2>

      {formData.acceptPlace === 'yes' && (
        <>
          <div class="border-b-2 pb-4">
            <p>You will need full Scout or Guide uniform to play in the concerts.</p>
            <br /><b><u>Scouts</u></b>
            <ul class="list-disc">
              <li class="ml-8 mb-2">Uniform shirt for your section with <b>navy</b> trousers or skirt (<b>not black</b>)</li>
              <li class="ml-8 mb-2">Smart black shoes</li>
            </ul>
            <br />
            <b><u>Guides - formal uniform</u></b>
            <ul class="list-disc">
              <li class="ml-8 mb-2">Inspire and All Leaders Over 18 – Pale blue uniform blouse with pink inner collar with <b>navy</b> trousers or skirt (not black)</li>
              <li class="ml-8 mb-2">Young Leaders and Inspire – Pale blue uniform blouse with grey inner collar with <b>navy</b> trousers or skirt (not black)</li>
              <li class="ml-8 mb-2">Rangers – Pale blue uniform blouse with aqua inner collar with <b>navy</b> trousers or skirt (not black)</li>
              <li class="ml-8 mb-2">Guides - Royal blue/red uniform polo shirt with <b>navy</b> trousers or skirt (not black)</li>
              <li class="ml-8 mb-2">Smart black shoes</li>
            </ul>
            <br />
            <p>I have read and understood the information above and also in the offer email about uniform requirements.</p>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="uniformAgreement"
                checked={formData.uniformAgreement}
                onChange={handleChange}
                className="mr-2"
                required
              />
              I Agree
            </label>
            {!formData.uniformAgreement && (
              <span className="text-red-500 text-sm block mt-1">
                You must agree to the uniform requirements
              </span>
            )}
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-bold mb-4">Clothing:</h2>
            <ul className="list-disc">
              <li className="ml-8 mb-2">If you are new we provide a polo top, necker and woggle.</li>
              <li className="ml-8 mb-2">If you have transferred ensembles or have changed size and your polo top no longer fits you we will also provide a new one.</li>
              <li className="ml-8 mb-2">If you have lost your polo top, necker or woggle then you can buy a replacement.</li>
              <li className="ml-8 mb-2">If you would like an extra polo top then you can buy one</li>
            </ul>
            <b><u>Choose one option below</u></b>
            <div className="space-y-2">
              {[
                { value: '1', label: 'I am a returning member to the same ensemble and I do not need any of the above clothing' },
                { value: '2', label: 'I am a new member and this is my first course ' },
                { value: '3', label: 'I am a returning member and have moved ensembles' },
                { value: '4', label: 'I have changed size since I was last at NSGSO/NSGCB. Please could I have a new polo top.  I agree to return my old top.' },
                { value: '5', label: 'I have lost any or all of my polo top, necker or woggle OR I would like a second polo top. Please could I have another one. I realise that I will need to pay for this.' },
                { value: '6', label: 'Other. Please explain' }
              ].map(option => (
                <label key={option.value} className="block">
                  <input
                    type="radio"
                    name="uniformType"
                    value={option.value}
                    checked={formData.uniformType === option.value}
                    onChange={handleUniformChange}
                    className="mr-2"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {formData.uniformType === '5' && (
            <>
              <div className="space-y-2">
                <p className="font-semibold">Have you lost your necker?</p>
                <div className="space-y-2">
                  <label className="block">
                    <input
                      type="radio"
                      name="neckerLost"
                      value="yes"
                      checked={formData.neckerLost === 'yes'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Yes (I understand that it will cost £6.50. You will be sent details of how to pay later.)
                  </label>
                  <label className="block">
                    <input
                      type="radio"
                      name="neckerLost"
                      value="no"
                      checked={formData.neckerLost === 'no'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
                {!formData.woggleLost && (
                  <span className="text-red-500 text-sm block">
                    Please indicate if you've lost your woggle
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <p className="font-semibold">Have you lost your woggle?</p>
                <div className="space-y-2">
                  <label className="block">
                    <input
                      type="radio"
                      name="woggleLost"
                      value="yes"
                      checked={formData.woggleLost === 'yes'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Yes (I understand that it will cost £1. You will be sent details of how to pay later.)
                  </label>
                  <label className="block">
                    <input
                      type="radio"
                      name="woggleLost"
                      value="no"
                      checked={formData.woggleLost === 'no'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
                {!formData.woggleLost && (
                  <span className="text-red-500 text-sm block">
                    Please indicate if you've lost your woggle
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <p className="font-semibold">Have you lost/would you like an extra polo top?</p>
                <div className="space-y-2">
                  <label className="block">
                    <input
                      type="radio"
                      name="poloTopLost"
                      value="yes"
                      checked={formData.poloTopLost === 'yes'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Yes (I understand that it will cost £12. You will be sent details of how to pay later.)
                  </label>
                  <label className="block">
                    <input
                      type="radio"
                      name="poloTopLost"
                      value="no"
                      checked={formData.poloTopLost === 'no'}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
                {!formData.poloTopLost && (
                  <span className="text-red-500 text-sm block">
                    Please indicate if you need a new polo top
                  </span>
                )}
              </div>

            </>
          )}

          {(formData.uniformType === '2' ||
            formData.uniformType === '3' ||
            formData.uniformType === '4' ||
            (formData.uniformType === '5' && formData.poloTopLost === 'yes')) && (
              <div className="space-y-2">
                <p className="font-semibold">Please note that all  polo tops, neckers and woggles will be provided on arrival at the course. Our polo tops need to be ordered in advance and are grey and unisex. What size do you need?</p>
                <div className="space-y-2">
                  {[
                    'XS (36")', 'S (38")', 'M (40")', 'L (42/44")',
                    'XL (46")', '2XL (48")', '3XL (50-52")', '4XL (54")'
                  ].map(size => (
                    <label key={size} className="block">
                      <input
                        type="radio"
                        name="poloSize"
                        value={size}
                        checked={formData.poloSize === size}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      {size}
                    </label>
                  ))}
                </div>
                {!formData.poloSize && (
                  <span className="text-red-500 text-sm block">
                    Please select a polo size
                  </span>
                )}
              </div>
            )}

          {formData.acceptPlace === 'yes' && (
            <div>
              <label className="block mb-2">
                Do you have any other queries about clothing of polo tops, neckers or woggles?
                <textarea
                  name="clothingQueries"
                  value={formData.clothingQueries}
                  onChange={handleChange}
                  className="w-full p-2 border rounded mt-1"
                />
              </label>
            </div>
          )}
        </>
      )}

      <div className="flex justify-between mt-4">
        <button
          onClick={prevStep}
          className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
        >
          Previous
        </button>
        <button
          onClick={nextStep}
          disabled={!isStepValid()}
          className={`py-2 px-4 rounded ${isStepValid()
            ? 'bg-nsg-pink text-white hover:bg-nsg-pale-blue'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

const CourseAvailability = ({ formData, handleChange, nextStep, prevStep }) => {
  const isStepValid = () => {
    // If not accepting the place, no validation needed
    if (formData.acceptPlace !== 'yes') return true;

    // Must have selected an availability option
    if (!formData.courseAvailability) return false;

    // If selected 'no' or 'unsure', must provide explanation
    if ((formData.courseAvailability === 'no' || formData.courseAvailability === 'unsure')
      && !formData.availabilityExplanation?.trim()) {
      return false;
    }

    return true;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4">Course Availability</h2>

      {formData.acceptPlace === 'yes' && (
        <>
          <div className="space-y-2">
            <p className="font-semibold">I am available for the whole course and I can arrive on Sunday 26th July 2026 by 2pm. I understand that it concludes on Sunday 2nd August 2026. (NB Members are allowed to leave after the Saturday evening concert although we recommend not to):</p>
            <div>
              <label className="block mb-2">
                <input
                  type="radio"
                  name="courseAvailability"
                  value="yes"
                  checked={formData.courseAvailability === 'yes'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Yes I can attend the whole course
              </label>
              <label className="block mb-2">
                <input
                  type="radio"
                  name="courseAvailability"
                  value="no"
                  checked={formData.courseAvailability === 'no'}
                  onChange={handleChange}
                  className="mr-2"
                />
                No I am not available for the whole course
              </label>
              <label className="block">
                <input
                  type="radio"
                  name="courseAvailability"
                  value="unsure"
                  checked={formData.courseAvailability === 'unsure'}
                  onChange={handleChange}
                  className="mr-2"
                />
                I am not sure about my plans at present
              </label>
            </div>
            {!formData.courseAvailability && (
              <span className="text-red-500 text-sm">Please select an availability option</span>
            )}
          </div>

          {(formData.courseAvailability === 'no' || formData.courseAvailability === 'unsure') && (
            <div>
              <label className="block mb-2">
                Please explain:
                <textarea
                  name="availabilityExplanation"
                  value={formData.availabilityExplanation || ''}
                  onChange={handleChange}
                  className={`w-full p-2 border rounded mt-1 ${!formData.availabilityExplanation?.trim() ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                />
                {!formData.availabilityExplanation?.trim() && (
                  <span className="text-red-500 text-sm">Please provide an explanation</span>
                )}
              </label>
            </div>
          )}
        </>
      )}

      <div className="flex justify-between mt-4">
        <button
          onClick={prevStep}
          className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
        >
          Previous
        </button>
        <button
          onClick={nextStep}
          disabled={!isStepValid()}
          className={`py-2 px-4 rounded ${isStepValid()
            ? 'bg-nsg-pink text-white hover:bg-nsg-pale-blue'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

const ReviewSubmit = ({ formData, prevStep, handleSubmit, isSubmitting, submitStatus }) => {
  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4">Review & Submit</h2>

      <div className="bg-gray-50 p-4 rounded">
        <h3 className="font-bold mb-2">Personal Details</h3>
        <p><b>Reference Number:</b> {formData.referenceNumber}</p>
        <p><b>Name:</b> {formData.firstName} {formData.surname}</p>
        <p><b>Email:</b> {formData.email}</p>
        {formData.parentEmail && <p><b>Parent's Email:</b> {formData.parentEmail}</p>}
      </div>

      <div className="bg-gray-50 p-4 rounded">
        <h3 className="font-bold mb-2">Course Selection</h3>
        <p><b>Accepting Place:</b> {formData.acceptPlace === 'yes' ? 'Yes' : 
                                  formData.acceptPlace === 'no' ? 'No' : 'Maybe'}</p>
        
        {formData.acceptPlace === 'yes' && (
          <>
            <p><b>Ensemble:</b> {formData.ensemble}</p>
            <p><b>Instruments:</b> {formData.instruments}</p>
          </>
        )}
        
        {formData.acceptPlace === 'no' && formData.declineReason && (
          <p><b>Reason for declining:</b> {formData.declineReason}</p>
        )}
        
        {formData.acceptPlace === 'maybe' && formData.maybeReason && (
          <p><b>Current situation:</b> {formData.maybeReason}</p>
        )}
      </div>

      {formData.acceptPlace === 'yes' && (
        <>
          <div className="bg-gray-50 p-4 rounded">
            <h3 className="font-bold mb-2">Uniform Requirements</h3>
            <p><b>Agreed to Requirements:</b> {formData.uniformAgreement ? 'Yes' : 'No'}</p>
            <p><b>Uniform Status:</b> {
              {
                '1': 'Returning member - no clothing needed',
                '2': 'New member - first course',
                '3': 'Returning member - moved ensembles',
                '4': 'Changed size since last attendance',
                '5': 'Lost items or want second polo top',
                '6': 'Other'
              }[formData.uniformType]
            }</p>
            {formData.uniformType === '5' && (
              <>
                <p><b>Necker Lost:</b> {formData.neckerLost}</p>
                <p><b>Woggle Lost:</b> {formData.woggleLost}</p>
                <p><b>Polo Top Lost/Extra:</b> {formData.poloTopLost}</p>
              </>
            )}
            {formData.poloSize && <p><b>Polo Size:</b> {formData.poloSize}</p>}
            {formData.clothingQueries && (
              <p><b>Clothing Queries:</b> {formData.clothingQueries}</p>
            )}
          </div>

          <div className="bg-gray-50 p-4 rounded">
            <h3 className="font-bold mb-2">Course Availability</h3>
            <p><b>Available for whole course:</b> {
              {
                'yes': 'Yes',
                'no': 'No',
                'unsure': 'Unsure'
              }[formData.courseAvailability]
            }</p>
            {formData.availabilityExplanation && (
              <p><b>Explanation:</b> {formData.availabilityExplanation}</p>
            )}
          </div>


          <div className="bg-gray-50 p-4 rounded">
            <h3 className="font-bold mb-2">Deposit Agreement</h3>
            <p><b>Agreed to Deposit Terms:</b> {formData.depositAgreement ? 'Yes' : 'No'}</p>
          </div>
        </>
      )}

      <div className="flex justify-between mt-8">
        <button
          onClick={prevStep}
          disabled={isSubmitting}
          className={`bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 
            ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Previous
        </button>
        
        <button
          onClick={onSubmit}
          disabled={isSubmitting || submitStatus?.success}
          className={`py-2 px-4 rounded ${
            isSubmitting 
              ? 'bg-blue-300 cursor-not-allowed'
              : submitStatus?.success
                ? 'bg-green-500 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600'
          } text-white`}
        >
          {isSubmitting ? 'Submitting...' : submitStatus?.success ? 'Submitted Successfully' : 'Submit Application'}
        </button>
      </div>

      {submitStatus?.error && (
        <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          Error: {submitStatus.error}
        </div>
      )}
      {submitStatus?.success && (
        <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          Your application has been successfully submitted. Thank you!
        </div>
      )}
    </div>
  );
};

const PhotoStep = ({ formData, handleChange, nextStep, prevStep }) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleChange({
        target: {
          name: 'photo',
          value: {
            file: file,
            name: file.name
          }
        }
      });
    }
  };


  const isStepValid = () => {
    if (!formData.photo) return false;
    return true;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4">Photo</h2>

      <div className="border-b-2 pb-2">
        <label className="block mb-2">
          <p>Each year we ask all members to provide a passport style photo (except you can smile) to us to help us identify you during the course. This can either be a passport photo or a photo that you have taken yourselves. Either way, we will use a passport-sized, head shot to share with all staff members, tutors and conductors as relevant to your position.</p>

          <br /><p>You need to upload a photo before you can go to the next page. This can be a very simple one that you just take of yourself right now.</p>

          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full p-2 border rounded mt-1"
            required
          />
          {formData.photo && (
            <span className="text-green-500 text-sm">
              Current file: {formData.photo.name}
            </span>
          )}
          {!formData.photo && (
            <span className="text-red-500 text-sm">Photo is required</span>
          )}
        </label>
      </div>




      <div className="flex justify-between mt-4">
        <button
          onClick={prevStep}
          className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
        >
          Previous
        </button>
        <button
          onClick={nextStep}
          disabled={!isStepValid()}
          className={`py-2 px-4 rounded ${isStepValid()
            ? 'bg-nsg-pink text-white hover:bg-nsg-pale-blue'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

const PaymentDeposit = ({ formData, handleChange, nextStep, prevStep }) => {
  const isStepValid = () => {
    // Only validate if user is accepting the place
    if (formData.acceptPlace !== 'yes') return true;

    // Must agree to deposit payment
    if (!formData.depositAgreement) return false;

    return true;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4">Payment of Deposit</h2>

      {formData.acceptPlace === 'yes' && (
        <div className="space-y-4">
          <div className="mt-6">
            <label className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="depositAgreement"
                checked={formData.depositAgreement}
                onChange={handleChange}
                className="mt-1"
              />
              <span>
                I understand that I need to pay a deposit of £95 by 27th December 2025 to secure my place,
                and that this deposit is non-refundable. I will make the bank transfer using the details provided in the acceptance email
                and use my reference number as the payment reference.
              </span>
            </label>
            {!formData.depositAgreement && (
              <span className="text-red-500 text-sm block mt-1">
                You must agree to the deposit payment terms to continue
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between mt-4">
        <button
          onClick={prevStep}
          className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600"
        >
          Previous
        </button>
        <button
          onClick={nextStep}
          disabled={!isStepValid()}
          className={`py-2 px-4 rounded ${isStepValid()
            ? 'bg-nsg-pink text-white hover:bg-nsg-pale-blue'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

const ProgressBar = ({ step, formData }) => {
  // Define all possible steps
  const allSteps = [
    'Personal Details',
    'Course Selection',
    'Uniform and Clothing',
    'Course Availability',
    'Photo and Photo Permission',
    'Payment of Deposit',
    'Review'
  ];

  // Filter steps based on acceptPlace response
  const activeSteps = formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe'
    ? ['Personal Details', 'Course Selection', 'Review']
    : allSteps;

  // Adjust step number for maybe/no responses when on review step
  let adjustedStep = step;
  if ((formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') && step === 4) {
    adjustedStep = 2;
  }

  // Calculate current progress percentage
  const progress = Math.min((adjustedStep / (activeSteps.length - 1)) * 100, 100);

  return (
    <div className="mb-8">
      {/* Full steps for larger screens */}
      <div className="hidden md:block">
        <div className="flex justify-between w-full mb-4">
          {activeSteps.map((stepName, index) => (
            <div
              key={stepName}
              className={`flex-1 text-center ${index < adjustedStep ? 'text-nsg-pink' :
                index === adjustedStep ? 'text-nsg-pink font-bold' :
                  'text-gray-400'
                }`}
            >
              <div className="relative">
                <div
                  className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center ${index <= adjustedStep ? 'bg-nsg-pink text-white' : 'bg-gray-200'
                    } `}
                >
                  {index + 1}
                </div>
                <div className="mt-2 text-sm">{stepName}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Condensed mobile view */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-4">
          {/* Current step indicator */}
          <div className="text-nsg-pink font-bold">
            Step {adjustedStep + 1} of {activeSteps.length}
          </div>
          {/* Current step name */}
          <div className="text-nsg-pink font-bold">
            {activeSteps[adjustedStep]}
          </div>
        </div>

        {/* Optional: Show previous and next step names */}
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <div>
            {adjustedStep > 0 && (
              <span className="opacity-75">
                Previous: {activeSteps[adjustedStep - 1]}
              </span>
            )}
          </div>
          <div>
            {adjustedStep < activeSteps.length - 1 && (
              <span className="opacity-75">
                Next: {activeSteps[adjustedStep + 1]}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar (shown on all screen sizes) */}
      <div className="relative pt-1">
        <div className="flex mb-2 items-center justify-between">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-nsg-pink h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    referenceNumber: '',
    firstName: '',
    surname: '',
    email: '',
    parentEmail: '',
    acceptPlace: '',
    ensemble: '',
    instruments: '',
    uniformAgreement: false,
    uniformType: '',
    neckerLost: '',
    woggleLost: '',
    poloTopLost: '',
    poloSize: '',
    clothingQueries: '',
    courseAvailability: '',
    availabilityExplanation: '',
    depositAgreement: false,
    otherQueries: '',
    declineReason: '',
    maybeReason: '',
    photo: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prevData => ({
      ...prevData,
      [name]: newValue
    }));

    if (name === 'acceptPlace' && (value === 'no' || value === 'maybe')) {
      setFormData(prevData => ({
        ...prevData,
        [name]: value,
        uniformAgreement: false,
        uniformType: '',
        neckerLost: '',
        woggleLost: '',
        poloTopLost: '',
        poloSize: '',
        clothingQueries: '',
        courseAvailability: '',
        availabilityExplanation: '',
        photo: null,
        depositAgreement: false
      }));
    }
  };

  const nextStep = () => {
    if (formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') {
      // If declining or maybe, skip to review after course selection
      if (step === 1) {
        setStep(4); // Jump to review step
      } else {
        setStep(prev => prev + 1);
      }
    } else {
      setStep(prev => prev + 1);
    }
    window.scrollTo(0, 0);
  };


  const prevStep = () => {
    if (formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') {
      // If declining or maybe, skip back to course selection from review
      if (step === 4) {
        setStep(1); // Jump back to course selection
      } else {
        setStep(prev => prev - 1);
      }
    } else {
      setStep(prev => prev - 1);
    }
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    try {
      if (isSubmitting || submitStatus?.success) return;
      setIsSubmitting(true);
      setSubmitStatus({ success: false, error: null });
  
      // Prepare form data
      const submissionData = { ...formData };
      
      // Only handle photo if accepting place
      if (formData.acceptPlace === 'yes' && formData.photo?.file) {
        try {
          // Photo upload logic...
          const urlResponse = await fetch('/submit/get-upload-url.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              referenceNumber: formData.referenceNumber,
              fileName: formData.photo.file.name,
              firstName: formData.firstName,
              lastName: formData.surname,
              ensemble: formData.ensemble,
              instruments: formData.instruments
            })
          });
  
          const urlData = await urlResponse.json();
          if (!urlData.success) throw new Error(urlData.error || 'Failed to get upload URL');
  
          const file = formData.photo.file;
          const uploadResponse = await fetch(urlData.uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type,
              'Content-Range': `bytes 0-${file.size-1}/${file.size}`
            },
            body: file
          });
  
          if (!uploadResponse.ok) throw new Error('Failed to upload photo');
          submissionData.photoPath = urlData.photoPath;
        } catch (error) {
          throw new Error(`Photo upload failed: ${error.message}`);
        }
      }
  
      delete submissionData.photo;
  
      // Submit form data
      const submitResponse = await fetch('/submit/submission-handler.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });
  
      const result = await submitResponse.json();
      if (!result.success) throw new Error(result.error || 'Form submission failed');
  
      setSubmitStatus({ success: true, error: null });
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus({ success: false, error: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    // Add your validation logic here
    // This is a basic example
    if (!formData.referenceNumber || !formData.firstName || !formData.surname || !formData.email) {
      alert('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <PersonalDetails
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
          />
        );
      case 1:
        return (
          <CourseSelection
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 2:
        return (formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') ? null : (
          <UniformRequirements
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 3:
        return (formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') ? null : (
          <CourseAvailability
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 4:
        return (formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') ? (
          <ReviewSubmit
            formData={formData}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitStatus={submitStatus}
          />
        ) : (
          <PhotoStep
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 5:
        return (formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') ? null : (
          <PaymentDeposit
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 6:
        return (formData.acceptPlace === 'no' || formData.acceptPlace === 'maybe') ? null : (
          <ReviewSubmit
            formData={formData}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitStatus={submitStatus}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-center mb-8">NSGSO/NSGCB Offer Confirmation 2026</h1>
      <ProgressBar step={step} formData={formData} />
      <div className="bg-white rounded-lg shadow-md p-6">
        {renderStep()}
      </div>
    </div>
  );
}